import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Star, Send, Zap } from "lucide-react";
import { toast } from "sonner";
import { getFeedbackStatus, submitFeedback } from "@/lib/api";

const CREDIT_THRESHOLD = 100;
const DISMISS_KEY = "mumble_feedback_dismissed";

const meshStyle = {
  background: `
    radial-gradient(ellipse at 15% 0%, rgba(249,115,22,0.2) 0%, transparent 50%),
    radial-gradient(ellipse at 85% 10%, rgba(59,130,246,0.18) 0%, transparent 50%),
    radial-gradient(ellipse at 50% 100%, rgba(249,115,22,0.12) 0%, transparent 45%),
    radial-gradient(ellipse at 0% 80%, rgba(37,99,235,0.1) 0%, transparent 45%),
    linear-gradient(160deg, #fff8f3 0%, #f0f5ff 45%, #fff7ed 75%, #eff4ff 100%)
  `,
};

const bannerMeshStyle = {
  background: `
    radial-gradient(ellipse at 0% 50%, rgba(249,115,22,0.4) 0%, transparent 55%),
    radial-gradient(ellipse at 100% 50%, rgba(59,130,246,0.35) 0%, transparent 55%),
    linear-gradient(135deg, #1e3a5f 0%, #1e40af 50%, #1a2744 100%)
  `,
};

export function FeedbackPrompt() {
  const [status, setStatus] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let mounted = true;
    const check = async () => {
      try {
        const res = await getFeedbackStatus();
        if (!mounted) return;
        const d = res.data;
        setStatus(d);
        if (d.feedback_submitted || d.credits > CREDIT_THRESHOLD) return;
        if (sessionStorage.getItem(DISMISS_KEY)) setShowBanner(true);
        else setShowModal(true);
      } catch { /* ignore */ }
    };
    check();
    const iv = setInterval(check, 30000);
    return () => { mounted = false; clearInterval(iv); };
  }, []);

  const dismiss = () => {
    setShowModal(false);
    setShowBanner(true);
    sessionStorage.setItem(DISMISS_KEY, "1");
  };

  const handleSubmit = async () => {
    if (!feedback.trim() || feedback.trim().length < 10) {
      toast.error("Please write at least 10 characters");
      return;
    }
    if (!rating) { toast.error("Please select a rating"); return; }
    setSubmitting(true);
    try {
      const res = await submitFeedback({ feedback: feedback.trim(), rating });
      setSuccess(true);
      toast.success(`+${res.data.credits_awarded} credits added!`);
      setTimeout(() => { setShowModal(false); setShowBanner(false); }, 2200);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally { setSubmitting(false); }
  };

  if (!status || status.feedback_submitted || status.credits > CREDIT_THRESHOLD) return null;

  return (
    <>
      {/* ── Floating Banner below navbar ── */}
      <AnimatePresence>
        {showBanner && !showModal && (
          <div
            className="fixed z-[25] left-0 right-0 flex justify-center pointer-events-none"
            style={{ top: "calc(env(safe-area-inset-top, 0px) + 4.5rem)" }}
          >
            <motion.div
              className="pointer-events-auto"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ type: "spring", damping: 22, stiffness: 300 }}
              data-testid="feedback-banner"
            >
            <button
              onClick={() => { setShowModal(true); setShowBanner(false); }}
              className="flex items-center gap-2.5 pl-3 pr-4 py-2.5 rounded-full text-white shadow-lg shadow-blue-900/25 hover:shadow-blue-900/40 transition-all group border border-white/10"
              style={bannerMeshStyle}
              data-testid="feedback-banner-btn"
            >
              <div className="p-1.5 bg-orange-500/30 rounded-full">
                <Zap className="w-3.5 h-3.5 text-orange-200 fill-orange-200" />
              </div>
              <span className="text-xs sm:text-sm font-medium whitespace-nowrap">
                Credits running low? <strong>Get 500 free</strong>
              </span>
              <span className="text-[11px] bg-white/15 px-2.5 py-0.5 rounded-full font-semibold group-hover:bg-white/25 transition whitespace-nowrap">
                Share feedback
              </span>
            </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ── Modal ── */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-start justify-center p-4"
            style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 3.5rem)" }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="feedback-modal-overlay"
          >
            <div className="absolute inset-0 bg-black/25 backdrop-blur-[2px]" onClick={dismiss} />

            <motion.div
              className="relative w-full max-w-sm rounded-2xl shadow-2xl shadow-orange-800/8 overflow-hidden border border-orange-100/50"
              style={meshStyle}
              initial={{ y: -30, opacity: 0, scale: 0.97 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: -30, opacity: 0, scale: 0.97 }}
              transition={{ type: "spring", damping: 28, stiffness: 350 }}
              data-testid="feedback-modal"
            >
              {success ? (
                <motion.div className="px-6 py-10 text-center" initial={{ scale: 0.9 }} animate={{ scale: 1 }}>
                  <div className="mx-auto w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mb-3">
                    <Zap className="w-7 h-7 text-orange-500 fill-orange-500" />
                  </div>
                  <p className="text-base font-bold text-slate-800" style={{ fontFamily: "Sora, sans-serif" }}>Thank you!</p>
                  <p className="text-sm text-slate-500 mt-1">500 credits added to your account</p>
                </motion.div>
              ) : (
                <>
                  {/* Header */}
                  <div className="flex items-center justify-between px-5 pt-5 pb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-orange-50 border border-orange-200/60">
                        <Zap className="w-5 h-5 text-orange-500 fill-orange-500" />
                      </div>
                      <div>
                        <h2 className="text-[15px] font-bold text-slate-900 leading-tight" style={{ fontFamily: "Sora, sans-serif" }}>
                          Credits running low?
                        </h2>
                        <p className="text-xs text-slate-500">Share quick feedback, get 500 credits instantly</p>
                      </div>
                    </div>
                    <button
                      onClick={dismiss}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100/60 transition"
                      data-testid="feedback-modal-close"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="px-5 pb-5 space-y-4">
                    {/* Stars */}
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Rate your experience</p>
                      <div className="flex gap-0.5" data-testid="feedback-star-rating">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button key={n}
                            onClick={() => setRating(n)}
                            onMouseEnter={() => setHoverRating(n)}
                            onMouseLeave={() => setHoverRating(0)}
                            className="p-1 transition-transform hover:scale-110 active:scale-95"
                            data-testid={`feedback-star-${n}`}
                          >
                            <Star className={`w-7 h-7 sm:w-8 sm:h-8 transition-colors ${
                              n <= (hoverRating || rating)
                                ? "fill-orange-400 text-orange-400 drop-shadow-sm"
                                : "text-slate-300/70 hover:text-slate-400"
                            }`} />
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Textarea */}
                    <textarea
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value.slice(0, 500))}
                      placeholder="What's working well? What could be better?"
                      rows={3}
                      className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-200/80 bg-white/70 backdrop-blur-sm focus:bg-white focus:border-blue-300 focus:ring-1 focus:ring-blue-200/50 outline-none resize-none transition-all placeholder:text-slate-400"
                      data-testid="feedback-textarea"
                    />

                    {/* Submit */}
                    <button
                      onClick={handleSubmit}
                      disabled={submitting || !feedback.trim() || !rating}
                      className="w-full h-10 flex items-center justify-center gap-2 rounded-xl text-sm font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:brightness-110 shadow-md shadow-blue-600/15"
                      style={{
                        background: "linear-gradient(135deg, #f97316 0%, #ea580c 40%, #2563eb 100%)",
                      }}
                      data-testid="feedback-submit-btn"
                    >
                      {submitting ? (
                        <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Submitting...</>
                      ) : (
                        <><Send className="w-3.5 h-3.5" /> Submit & Claim 500 Credits</>
                      )}
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
